import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment-timezone";

import { T } from "../../constants";
import { cx, useUi } from "../../styles";
import Panel from "../../components/Panel";
import useVenueDayRollover from "../useVenueDayRollover";
import { blackoutCoverage } from "../../blackouts/blackoutHelpers";
import {
  dayLabel,
  localDay,
  monthGrid,
  monthOf,
  shiftDay,
} from "./operationsPreviewData";

const CARD_STYLE = {
  boxShadow: "none",
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  height: 560,
  boxSizing: "border-box",
  overflowY: "auto",
};

const MONTH = "MONTH";
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const useStyles = makeStyles({
  panel: {
    padding: "16px 20px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: T.subtleFg,
    fontWeight: 600,
  },
  periods: {
    display: "flex",
    gap: 2,
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    padding: 2,
  },
  period: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    borderRadius: 6,
    padding: "4px 10px",
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    '&[aria-pressed="true"]': {
      background: T.accent,
      color: T.accentFg,
      fontWeight: 600,
    },
  },
  rows: { display: "flex", flexDirection: "column", gap: 2 },
  row: {
    display: "grid",
    gridTemplateColumns: "96px 1fr 52px 56px",
    gap: "8px 12px",
    alignItems: "center",
    fontSize: 13,
    width: "100%",
    border: "none",
    background: "transparent",
    padding: "5px 6px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    textAlign: "left",
    "&:hover": { background: T.surface },
    '&[aria-pressed="true"]': { background: T.accent },
    "&:disabled": {
      cursor: "default",
      opacity: 0.58,
      background: T.surface,
    },
  },
  time: {
    color: T.mutedFg,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  track: {
    height: 6,
    borderRadius: 999,
    background: T.muted,
    overflow: "hidden",
  },
  fill: { height: "100%", background: T.primary, borderRadius: 999 },
  fillFull: { background: T.primaryDark },
  used: {
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
  },
  left: {
    fontSize: 12,
    color: T.mutedFg,
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  full: { color: T.primary, fontWeight: 600 },
  blackout: { color: T.subtleFg },
  inactive: { color: T.subtleFg, fontWeight: 600 },
  stepper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "8px 10px",
    border: `1px solid ${T.border}`,
    borderRadius: 8,
    fontSize: 13,
    background: T.elevated,
  },
  stepperControls: { display: "flex", alignItems: "center", gap: 8 },
  stepValue: {
    minWidth: 24,
    textAlign: "center",
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  tiny: { padding: "4px 10px", fontSize: 12 },
  hint: { fontSize: 12, color: T.mutedFg },
  monthHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 12,
    fontSize: 12,
    color: T.mutedFg,
  },
  monthName: { fontSize: 14, fontWeight: 600, color: T.fg },
  calendar: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: 4,
  },
  dow: {
    fontSize: 11,
    color: T.subtleFg,
    textAlign: "center",
    padding: "2px 0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  day: {
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 8,
    padding: "6px 6px 5px",
    minHeight: 50,
    background: T.elevated,
    cursor: "pointer",
    fontFamily: "inherit",
    color: T.fg,
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    "&:hover": { background: T.surface },
    "&:disabled": {
      cursor: "default",
      color: T.subtleFg,
      background: T.surface,
    },
    '&[aria-current="date"]': { borderColor: T.primary },
  },
  dayNumber: { fontSize: 12, fontWeight: 600 },
  dayCounts: {
    fontSize: 11,
    color: T.mutedFg,
    fontVariantNumeric: "tabular-nums",
  },
  dayPickups: { color: T.primary, fontWeight: 600 },
  blank: { minHeight: 50 },
});

function shiftContainsMinute(shift, minute) {
  if (shift.endTime > shift.startTime) {
    return minute >= shift.startTime && minute < shift.endTime;
  }

  return minute >= shift.startTime || minute < shift.endTime;
}

const pad = (value) => String(value).padStart(2, "0");

function emptyHourlyRows() {
  return Array.from({ length: 24 }, (_, hour) => ({
    key: `${pad(hour)}:00 – ${pad((hour + 1) % 24)}:00`,
    startMinute: hour * 60,
    used: 0,
  }));
}

function daysForKind(forecast, kind) {
  return forecast?.[kind]?.days ?? [];
}

function hourFor(instant, timezone) {
  // Capacity timestamps are returned in the installation's local time. Using
  // moment.tz directly keeps a timezone-less 09:15 value in the 09:00 bucket
  // instead of first interpreting it in the browser's timezone.
  const value = timezone ? moment.tz(instant, timezone) : moment(instant);
  return value.isValid() ? value.hour() : null;
}

export default function PickupCapacityPanel({
  now = Date.now(),
  shifts = [],
  blackouts = [],
  forecast,
  loading = false,
  error,
  settingsLoading = false,
  settingsError,
  timezone,
}) {
  const s = useStyles();
  const ui = useUi();
  const currentVenueDay = timezone
    ? moment(now).tz(timezone).format("YYYY-MM-DD")
    : localDay(now);
  const [today, setToday] = useState(currentVenueDay);
  const previousVenueDay = useRef(currentVenueDay);
  const [period, setPeriod] = useState(currentVenueDay);
  const [view, setView] = useState("DAY");
  const [selected, setSelected] = useState(null);
  const [kind, setKind] = useState("pickups");

  useEffect(() => {
    const previousDay = previousVenueDay.current;
    if (currentVenueDay === previousDay) return;

    previousVenueDay.current = currentVenueDay;
    setToday(currentVenueDay);
    setPeriod((selectedDay) =>
      selectedDay === previousDay ? currentVenueDay : selectedDay,
    );
  }, [currentVenueDay]);

  useVenueDayRollover(timezone, (nextDay, previousDay) => {
    previousVenueDay.current = nextDay;
    setToday(nextDay);
    setPeriod((selectedDay) =>
      selectedDay === previousDay ? nextDay : selectedDay,
    );
  });
  const labelForDay = (day) => {
    if (day === today) return "Today";
    if (day === shiftDay(today, 1)) return "Tomorrow";
    return dayLabel(day, now);
  };
  const segments = [today, shiftDay(today, 1), shiftDay(today, 2)];
  const pickups = kind === "pickups";
  const noun = pickups ? "pickups" : "arrivals to park";
  const monthBase = monthGrid(monthOf(today), now, today);
  const pickupDays = daysForKind(forecast, "pickups");
  const parkingDays = daysForKind(forecast, "parking");
  const pickupByDate = new Map(pickupDays.map((day) => [day.date, day]));
  const parkingByDate = new Map(parkingDays.map((day) => [day.date, day]));
  const month = {
    ...monthBase,
    days: monthBase.days.map((entry) => ({
      ...entry,
      arrivals: parkingByDate.get(entry.day)?.entries?.length ?? 0,
      departures: pickupByDate.get(entry.day)?.entries?.length ?? 0,
    })),
  };
  const rows = useMemo(
    () => {
      const day = daysForKind(forecast, kind).find((item) => item.date === period);
      const hourlyUse = (day?.entries ?? []).reduce((counts, entry) => {
        const hour = hourFor(entry.scheduledAt, timezone);
        if (hour !== null) counts[hour] += 1;
        return counts;
      }, Array(24).fill(0));

      return emptyHourlyRows().map((row, hour) => {
        const shift = shifts.find((item) => shiftContainsMinute(item, row.startMinute));
        const slotMinutes = shift?.slotMinutes ?? 15;
        const perSlotMax = pickups
          ? shift?.maxCollectPerSlot ?? row.perSlotMax
          : shift?.maxParkPerSlot ?? row.perSlotMax;
        const coverage = blackoutCoverage(
          blackouts,
          period,
          row.startMinute,
          60,
          timezone,
        );

        return {
          ...row,
          used: hourlyUse[hour],
          operating: Boolean(shift),
          slotMinutes,
          perSlotMax,
          max: shift ? perSlotMax * (60 / slotMinutes) : 0,
          overridden: Boolean(shift),
          blackout: coverage === "FULL",
          partialBlackout: coverage === "PARTIAL",
        };
      });
    },
    [blackouts, forecast, kind, period, pickups, shifts, timezone],
  );
  const selectedRow = rows.find((row) => row.key === selected) ?? null;

  const choose = (day) => {
    setPeriod(day);
    setView("DAY");
    setSelected(null);
  };

  return (
    <Panel className={s.panel} style={CARD_STYLE} aria-label="Pickup capacity">
      <div className={s.head}>
        <span className={s.eyebrow}>
          {pickups ? "Pickup" : "Parking"} capacity
          {view === "DAY" && !segments.includes(period)
            ? ` · ${labelForDay(period)}`
            : ""}
        </span>

        <div className={s.periods} role="group" aria-label="Capacity kind">
          <button
            type="button"
            className={s.period}
            aria-pressed={pickups}
            onClick={() => {
              setKind("pickups");
              setSelected(null);
            }}
          >
            Pickups
          </button>
          <button
            type="button"
            className={s.period}
            aria-pressed={!pickups}
            onClick={() => {
              setKind("parking");
              setSelected(null);
            }}
          >
            Parking
          </button>
        </div>

        <div className={s.periods} role="group" aria-label="Capacity period">
          {segments.map((day) => (
            <button
              key={day}
              type="button"
              className={s.period}
              aria-pressed={view === "DAY" && period === day}
              onClick={() => choose(day)}
            >
              {labelForDay(day)}
            </button>
          ))}
          <button
            type="button"
            className={s.period}
            aria-pressed={view === MONTH}
            onClick={() => {
              setView(MONTH);
              setSelected(null);
            }}
          >
            Month
          </button>
        </div>
      </div>

      {(loading || settingsLoading) && (
        <div className={s.hint}>Loading capacity configuration and demand…</div>
      )}
      {(error || settingsError) && (
        <div className={s.hint}>Could not load capacity configuration or demand.</div>
      )}

      {!settingsLoading && !settingsError && (view === MONTH ? (
        <>
          <div className={s.monthHead}>
            <span className={s.monthName}>{month.label}</span>
            <span>
              Arrivals · <span className={s.dayPickups}>pickups</span> per day.
              Click a day for its hourly view.
            </span>
          </div>
          <div
            className={s.calendar}
            role="grid"
            aria-label={`${month.label} capacity calendar`}
          >
            {DAYS_OF_WEEK.map((label) => (
              <span key={label} className={s.dow} role="columnheader">
                {label}
              </span>
            ))}
            {Array.from({ length: month.leading }, (_, index) => (
              <span key={`blank-${index}`} className={s.blank} aria-hidden="true" />
            ))}
            {month.days.map((entry) => (
              <button
                key={entry.day}
                type="button"
                role="gridcell"
                className={s.day}
                disabled={entry.past}
                aria-current={entry.today ? "date" : undefined}
                aria-label={`${entry.day}: ${entry.arrivals} arrivals, ${entry.departures} pickups`}
                onClick={() => choose(entry.day)}
              >
                <span className={s.dayNumber}>{entry.number}</span>
                {(entry.arrivals > 0 || entry.departures > 0) && (
                  <span className={s.dayCounts}>
                    {entry.arrivals} ·{" "}
                    <span className={s.dayPickups}>{entry.departures}</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div
          className={s.rows}
          aria-label={`Hourly ${pickups ? "pickup" : "parking"} capacity for ${labelForDay(period)}`}
        >
          {rows.map((row) => {
            const ratio = row.max ? Math.min(1, row.used / row.max) : 0;
            const full = row.max > 0 && row.used >= row.max;
            const left = Math.max(0, row.max - row.used);
            const open = selected === row.key;

            return (
              <React.Fragment key={row.key}>
                <button
                  type="button"
                  className={s.row}
                  aria-pressed={open}
                  disabled={!row.operating || row.blackout}
                  aria-label={`${row.key} ${row.blackout ? "blackout" : row.partialBlackout ? "partial blackout" : row.operating ? "active" : "inactive"}`}
                  onClick={() => setSelected(open ? null : row.key)}
                >
                  <span className={s.time}>{row.key}</span>
                  <span
                    className={s.track}
                    role="img"
                    aria-label={`${row.key} ${row.used} of ${row.max} ${noun}`}
                  >
                    <span
                      className={`${s.fill} ${full ? s.fillFull : ""}`}
                      style={{ display: "block", width: `${ratio * 100}%` }}
                    />
                  </span>
                  <span className={s.used}>
                    {row.operating ? `${row.used} / ${row.max}` : `${row.used} / —`}
                  </span>
                  <span
                    className={`${s.left} ${
                      row.blackout
                        ? s.blackout
                        : row.partialBlackout
                          ? s.blackout
                        : !row.operating
                          ? s.inactive
                          : full
                            ? s.full
                            : ""
                    }`}
                  >
                    {row.blackout
                      ? "Blackout"
                      : row.partialBlackout
                        ? "Partial blackout"
                      : !row.operating
                        ? "Inactive"
                        : full
                          ? "Full"
                          : `${left} left`}
                  </span>
                </button>

                {open && (
                  <div className={s.stepper} aria-label={`Adjust ${row.key}`}>
                    <span>
                      Max {noun} per {selectedRow.slotMinutes}-minute period this hour
                    </span>
                    <span className={s.stepperControls}>
                      <span className={s.stepValue}>{row.perSlotMax}</span>
                      <button
                        type="button"
                        className={cx(ui.primaryBtn, s.tiny)}
                        onClick={() => setSelected(null)}
                      >
                        Done
                      </button>
                    </span>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </Panel>
  );
}
